const wordCharacter = /[\p{L}\p{M}\p{N}_]/u;
const escaped = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function findLiteralText(text, query, { matchCase = false, wholeWords = false, limit = 10000 } = {}) {
  if (!query) return { matches: [], truncated: false };
  const pattern = new RegExp(escaped(query), matchCase ? 'gu' : 'giu'), matches = [];
  let found;
  while ((found = pattern.exec(text))) {
    const before = Array.from(text.slice(Math.max(0, found.index - 2), found.index)).pop() || '', after = Array.from(text.slice(found.index + found[0].length, found.index + found[0].length + 2))[0] || '';
    if (wholeWords && (wordCharacter.test(before) || wordCharacter.test(after))) continue;
    if (matches.length === limit) return { matches, truncated: true };
    matches.push({ index: found.index, length: found[0].length, text: found[0] });
  }
  return { matches, truncated: false };
}

// Model text is searchable across bold/link/italic attribute boundaries, while
// paragraph, table cell, image and soft-break boundaries remain separate.
export function findRichText(editor, query, options = {}) {
  if (!editor || editor.state !== 'ready' || !query) return { matches: [], truncated: false };
  const model = editor.model, matches = [], limit = options.limit || 10000;
  let truncated = false;
  const visit = parent => {
    let text = '', start = 0;
    const flush = () => {
      const result = findLiteralText(text, query, { ...options, limit: Math.max(0, limit - matches.length) });
      for (const match of result.matches) {
        const from = model.createPositionAt(parent, start + match.index), to = model.createPositionAt(parent, start + match.index + match.length);
        matches.push({ range: model.createRange(from, to), text: match.text, version: model.document.version });
      }
      truncated ||= result.truncated; text = '';
    };
    for (const child of parent.getChildren()) {
      if (child.is('$text')) { if (!text) start = child.startOffset; text += child.data; }
      else { flush(); if (child.getChildren) visit(child); }
      if (truncated) break;
    }
    if (!truncated) flush();
  };
  for (const name of model.document.getRootNames()) { visit(model.document.getRoot(name)); if (truncated) break; }
  return { matches, truncated };
}

export function replaceRichText(editor, matches, replacement) {
  if (!editor || editor.state !== 'ready' || editor.isReadOnly) return 0;
  if (!matches.length) return 0;
  if (typeof replacement !== 'string' || replacement.length * matches.length > 1000000) throw new Error('This replacement is too large. Use a shorter replacement or fewer matches.');
  const model = editor.model;
  for (const match of matches) {
    const text = Array.from(match.range.getItems()).map(item => item.is('$textProxy') || item.is('$text') ? item.data : '').join('');
    if (match.version !== model.document.version || text !== match.text) throw new Error('The content changed. Find the text again before replacing it.');
  }
  model.change(writer => {
    // Back-to-front edits keep all original model offsets valid. One batch makes
    // Replace all a single Undo operation; no HTML string or markup is edited.
    for (const { range } of [...matches].reverse()) {
      const from = range.start, node = from.textNode || from.nodeAfter;
      const attributes = node?.is('$text') ? Object.fromEntries(node.getAttributes()) : {};
      writer.remove(range);
      if (replacement) writer.insertText(replacement, attributes, from);
      writer.setSelection(model.createPositionAt(from.parent, from.offset + replacement.length));
    }
  });
  return matches.length;
}

// Legacy editor callers pass literal config objects and upload-plugin closures
// on every render. Equivalent config must not tear down the editor/caret.
export function richEditorConfigKey(value, seen = new Set()) {
  if (typeof value === 'function') return 'function:' + value.toString();
  if (!value || typeof value !== 'object') return JSON.stringify(value);
  if (seen.has(value)) return '[circular]';
  const next = new Set(seen); next.add(value);
  if (Array.isArray(value)) return '[' + value.map(item => richEditorConfigKey(item, next)).join(',') + ']';
  return '{' + Object.keys(value).sort().map(key => JSON.stringify(key) + ':' + richEditorConfigKey(value[key], next)).join(',') + '}';
}
